import { useState } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { useHaloWebhook, useRotateHaloWebhookPassword, type PsaConnection } from '@/hooks/usePsa.ts';
import { useConfirm } from '@/hooks/useConfirm.tsx';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { Button } from '@/components/ui/button.tsx';

/**
 * Witina's webhook for a Halo connection, which the admin adds in Halo by hand
 * (docs/PsaIntegration.md, "Connecting"). Halo signs nothing, so it proves itself
 * with Basic authentication: a password per connection, long and random.
 *
 * The details include the password, and each read is kept in the activity log, so
 * they're fetched when the admin asks to see them, not whenever the tab opens.
 */
export function HaloWebhook({ connection }: { connection: PsaConnection }) {
  const [shown, setShown] = useState(false);
  const setup = useHaloWebhook(connection.id, shown);
  const rotate = useRotateHaloWebhookPassword(connection.id);
  const { confirm, confirmDialog } = useConfirm();
  const details = rotate.data ?? setup.data;

  const handleRotate = async () => {
    const ok = await confirm({
      title: 'Make a new password?',
      message: `The old one stops working at once. Until the webhook in ${connection.name} has the new one, changes reach Witina within 5 minutes instead of within a minute.`,
      confirmText: 'New password',
      isDanger: true,
    });
    if (ok) {
      rotate.mutate(undefined, { onSuccess: () => { setShown(true); } });
    }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      {confirmDialog}
      <p className="text-sm text-muted-foreground">
        Halo tells Witina when a ticket changes through a webhook you add in Halo, under Configuration › Integrations ›
        Webhooks. Without it, changes still reach Witina within 5 minutes.
      </p>
      <ol className="text-sm list-decimal pl-5 space-y-1">
        <li>Add a standard webhook with the payload URL below and the POST method.</li>
        <li>Choose Basic authentication, with the username and password below.</li>
        <li>Choose the payload “Event information only”, and one delivery per occurrence.</li>
        <li>Add the events for ticket updates, status changes and new actions.</li>
      </ol>
      {details ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Payload URL</dt>
          <dd><Copyable text={details.url} label="Copy the payload URL" /></dd>
          <dt className="text-muted-foreground">Username</dt>
          <dd><Copyable text={details.username} label="Copy the username" /></dd>
          <dt className="text-muted-foreground">Password</dt>
          <dd><Copyable text={details.password} label="Copy the password" /></dd>
        </dl>
      ) : (
        <Button variant="outline" size="sm" disabled={setup.isFetching} onClick={() => { setShown(true); }}>
          {setup.isFetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Show the URL and password
        </Button>
      )}
      {setup.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(setup.error, 'Couldn’t read the webhook details.')}</p>
      ) : null}
      {rotate.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(rotate.error, 'Couldn’t make a new password.')}</p>
      ) : null}
      <Button variant="ghost" size="sm" disabled={rotate.isPending} onClick={() => { void handleRotate(); }}>
        {rotate.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        New password
      </Button>
    </div>
  );
}

function Copyable({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => { setCopied(false); }, 2000);
      },
      () => {
        // Clipboard access can be refused. The text is on screen and selectable
        // either way, so don't claim a copy that didn't happen.
      },
    );
  };
  return (
    <span className="flex items-center gap-2 min-w-0">
      <code className="font-mono text-xs break-all select-all">{text}</code>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label={label} onClick={copy}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </span>
  );
}
